// Automatic cloud copy of progress. localStorage dies with a deleted
// Home-Screen icon, so the same payload as the Settings Backup file is pushed
// to a Netlify blob (netlify/functions/progress.mjs) and pulled back silently
// on a fresh install. Manual Backup/Restore stays as the safety net.
//
// Multi-user (2026-07-14): each device syncs under its own short code (blob
// key "latest-<code>"), so two people on the same app never overwrite each
// other. The code is generated at the first push, shown in Settings, and
// typed once on a new device (or passed as ?sync=CODE) to claim the progress.
// The plain "latest" blob is a bootstrap mirror (last pusher), so a one-user
// app still restores with no typing at all.
const APP_ID = 'formosan-reader';

// The one list of keys that IS "the progress" — the Backup button uses it too.
export const BACKUP_KEYS = [
  'seediq_schema_version', 'seediq_vocab_v1', 'seediq_progress_v1',
  'seediq_gloss_v1', 'seediq_fontscale_v1', 'srs_direction',
  'seediq_sync_code_v1',   // rides along so a restored backup re-links its cloud slot
];

const FN = '/.netlify/functions/progress';
const LAST_PUSH = 'seediq_cloud_lastpush_v1';   // fingerprint of last successful push
const PULL_TRIED = 'seediq_cloud_pulled';       // one restore attempt per session — no reload loops
const SYNC_CODE = 'seediq_sync_code_v1';
const CODE_RE = /^[A-Z0-9]{4,16}$/;
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';  // no 0/O 1/I/L lookalikes

export function getSyncCode() { return localStorage.getItem(SYNC_CODE) || ''; }

// A sync code names a storage slot; it is not a secret and guards nothing, so
// Math.random is an acceptable fallback where window.crypto is missing. What is
// NOT acceptable is what the bare crypto call did: throw, get swallowed by
// pushProgress's catch, and leave the device silently never syncing. Every
// browser this ships to has crypto — but "the code generator threw" must not be
// a way to lose a speaker's corrections.
function randomCode() {
  const n = 6;
  const g = typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues
    ? Array.from(window.crypto.getRandomValues(new Uint8Array(n)))
    : Array.from({ length: n }, () => Math.floor(Math.random() * 256));
  return g.map((b) => ALPHABET[b % ALPHABET.length]).join('');
}

export function ensureSyncCode() {
  let c = getSyncCode();
  if (!c) {
    c = randomCode();
    localStorage.setItem(SYNC_CODE, c);
  }
  return c;
}

// "Has this device anything worth keeping?" — the guard on both pushing (never
// clobber the cloud with an empty state) and pulling (never restore over real
// work).
//
// It counted VOCAB only, and that silently lost the contributions that matter
// most. A speaker who opens the app, corrects twenty glosses and never marks a
// single word 學習中 has no vocab — so nothing was ever pushed, and the
// corrections lived and died in one browser's localStorage. Glosses count.
const REAL_KEYS = ['seediq_vocab_v1', 'seediq_gloss_v1'];

function hasRealProgress() {
  return REAL_KEYS.some((k) => {
    const v = localStorage.getItem(k);
    return !!v && v !== '{}';
  });
}

function payload() {
  const values = {};
  BACKUP_KEYS.forEach((k) => { const v = localStorage.getItem(k); if (v != null) values[k] = v; });
  return { app: APP_ID, saved: new Date().toISOString(), values };
}

export async function pushProgress() {
  try {
    // Only devices with REAL progress may write. A fresh browser (or a test
    // visit on the PC) holds just the schema key — pushing that would clobber
    // the learner's words in the cloud with an empty state.
    if (!hasRealProgress()) return;
    const code = ensureSyncCode();   // into localStorage BEFORE payload() so it rides along
    const p = payload();
    const fingerprint = JSON.stringify(p.values);     // timestamp excluded on purpose
    if (localStorage.getItem(LAST_PUSH) === fingerprint) return;
    const r = await fetch(`${FN}?code=${code}`, { method: 'PUT', body: JSON.stringify(p), keepalive: true });
    if (r.ok) localStorage.setItem(LAST_PUSH, fingerprint);
  } catch (_) {} // offline — next tick will retry
}

// Fresh install: nothing here, but the cloud has something → restore and reload.
// A device that already has a code asks for ITS blob; a brand-new device asks
// for the bootstrap mirror and adopts the code it finds inside.
async function pullIfFresh() {
  try {
    if (hasRealProgress()) return;
    if (sessionStorage.getItem(PULL_TRIED)) return;
    sessionStorage.setItem(PULL_TRIED, '1');
    const code = getSyncCode();
    const r = await fetch(code ? `${FN}?code=${code}` : FN, { cache: 'no-store' });
    if (!r.ok) return;
    const data = await r.json();
    if (!data || data.app !== APP_ID || !data.values) return;
    const entries = Object.entries(data.values);
    if (!entries.length) return;
    entries.forEach(([k, v]) => localStorage.setItem(k, v));
    localStorage.setItem(LAST_PUSH, JSON.stringify(data.values));
    window.location.reload();
  } catch (_) {}
}

// Deliberate identity switch (Settings' "enter a code", ?sync=CODE links).
// This device's progress is pushed under its OLD code first, so nothing is
// lost; then the new code's blob (if any) replaces the local state.
export async function adoptSyncCode(raw) {
  const code = String(raw || '').trim().toUpperCase();
  if (!CODE_RE.test(code)) return 'bad-code';
  if (code === getSyncCode()) return 'same';
  await pushProgress();
  localStorage.setItem(SYNC_CODE, code);
  localStorage.removeItem(LAST_PUSH);
  try {
    const r = await fetch(`${FN}?code=${code}`, { cache: 'no-store' });
    if (r.ok) {
      const data = await r.json();
      if (data && data.app === APP_ID && data.values && Object.keys(data.values).length) {
        Object.entries(data.values).forEach(([k, v]) => localStorage.setItem(k, v));
        localStorage.setItem(SYNC_CODE, code);   // the typed code wins over any code inside the blob
        localStorage.setItem(LAST_PUSH, JSON.stringify(data.values));
        return 'restored';
      }
    }
  } catch (_) {}
  return 'claimed';   // nothing stored under it yet — this device's pushes will fill it
}

export function startCloudSync() {
  let boot = Promise.resolve();
  try {
    // ?sync=CODE link: claim that cloud slot — but only on a device with no
    // real progress yet; a device WITH progress switches codes in Settings,
    // where the old code is shown before it is replaced.
    const c = new URLSearchParams(window.location.search).get('sync');
    if (c && !hasRealProgress()) {
      boot = adoptSyncCode(c).then((res) => { if (res === 'restored') window.location.reload(); });
    }
  } catch (_) {}
  boot.then(pullIfFresh).then(pushProgress);
  const t = setInterval(pushProgress, 90e3);
  // A hidden page may be the last we see of this session — flush now
  // (keepalive lets the PUT finish after the page is gone).
  const onVis = () => { if (document.visibilityState === 'hidden') pushProgress(); };
  document.addEventListener('visibilitychange', onVis);
  return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
}
